import {
  AfterContentInit,
  Component,
  ContentChildren,
  EventEmitter,
  Input,
  OnDestroy,
  Output,
  QueryList,
} from '@angular/core';
import { Subscription } from 'rxjs';
import {
  PortalSortDirection,
  PortalTableColumn,
  PortalTableSortChange,
} from './portal-table.model';
import { PortalTableCellDirective } from './portal-table-cell.directive';

@Component({
  selector: 'app-portal-table',
  templateUrl: './portal-table.component.html',
  styleUrls: ['./portal-table.component.scss'],
})
export class PortalTableComponent<T extends object = object> implements AfterContentInit, OnDestroy {
  @Input() columns: PortalTableColumn[] = [];
  @Input() rows: T[] = [];
  @Input() loading = false;
  @Input() loadingRows = 8;
  @Input() emptyTitle = 'No records found';
  @Input() emptyDescription = '';
  @Input() emptyActionLabel = '';
  @Input() sortActive = '';
  @Input() sortDirection: PortalSortDirection = '';
  @Input() rowClickable = true;
  @Input() trackByKey = '';

  @Output() rowClick = new EventEmitter<T>();
  @Output() sortChange = new EventEmitter<PortalTableSortChange>();
  @Output() emptyAction = new EventEmitter<void>();

  @ContentChildren(PortalTableCellDirective)
  cellTemplates?: QueryList<PortalTableCellDirective>;
  private cellTemplateMap = new Map<string, PortalTableCellDirective>();
  private cellTemplateChanges?: Subscription;

  ngAfterContentInit(): void {
    this.rebuildCellTemplateMap();
    this.cellTemplateChanges = this.cellTemplates?.changes.subscribe(() => this.rebuildCellTemplateMap());
  }

  ngOnDestroy(): void {
    this.cellTemplateChanges?.unsubscribe();
  }

  get gridTemplateColumns(): string {
    if (!this.columns.length) {
      return '1fr';
    }

    return this.columns
      .map(column => column.width || 'minmax(120px, 1fr)')
      .join(' ');
  }

  get skeletonRows(): number[] {
    return Array.from({ length: this.loadingRows }, (_item, index) => index);
  }

  templateFor(columnKey: string): PortalTableCellDirective | undefined {
    return this.cellTemplateMap.get(columnKey);
  }

  cellValue(row: T, column: PortalTableColumn): unknown {
    return (row as Record<string, unknown>)?.[column.key] ?? '';
  }

  cellClass(column: PortalTableColumn): string {
    return `portal-table__cell--${column.align || 'left'}`;
  }

  headerClass(column: PortalTableColumn): string {
    return `portal-table__header-cell--${column.align || 'left'}`;
  }

  toggleSort(column: PortalTableColumn): void {
    if (!column.sortable) {
      return;
    }

    let direction: PortalSortDirection = 'asc';

    if (this.sortActive === column.key) {
      direction = this.sortDirection === 'asc' ? 'desc' : this.sortDirection === 'desc' ? '' : 'asc';
    }

    this.sortChange.emit({
      active: direction ? column.key : '',
      direction,
    });
  }

  sortIcon(column: PortalTableColumn): string {
    if (this.sortActive !== column.key || !this.sortDirection) {
      return 'unfold_more';
    }

    return this.sortDirection === 'asc' ? 'keyboard_arrow_up' : 'keyboard_arrow_down';
  }

  emitRowClick(row: T): void {
    if (this.rowClickable) {
      this.rowClick.emit(row);
    }
  }

  onRowKeydown(event: KeyboardEvent, row: T): void {
    if (!this.rowClickable || (event.key !== 'Enter' && event.key !== ' ')) {
      return;
    }

    event.preventDefault();
    this.emitRowClick(row);
  }

  trackByColumn(_index: number, column: PortalTableColumn): string {
    return column.key;
  }

  trackByRow(index: number, row: T): unknown {
    const record = row as Record<string, unknown>;
    return this.trackByKey
      ? record[this.trackByKey] ?? index
      : record['id'] ?? record['transactionId'] ?? record['document_id'] ?? record['invoiceNumber'] ?? index;
  }

  trackByIndex(index: number): number {
    return index;
  }

  private rebuildCellTemplateMap(): void {
    this.cellTemplateMap.clear();
    this.cellTemplates?.forEach(template => {
      if (template.columnKey) {
        this.cellTemplateMap.set(template.columnKey, template);
      }
    });
  }
}
